import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Bell, Check, X, Clock, PencilSimple, Plus, Trash, Prohibit } from "@phosphor-icons/react";

// Editable fields per entity type (used by the "Edit before approve" dialog).
const FIELD_DEFS = {
  product: [
    { key: "name", label: "Name", type: "text" },
    { key: "base_sku", label: "Base SKU", type: "text" },
    { key: "color_hex", label: "Colour", type: "hex" },
    { key: "manufacturer_id", label: "Manufacturer", type: "manufacturer" },
    { key: "series_id", label: "Series", type: "series" },
    { key: "inventory_type", label: "Type", type: "invtype" },
    { key: "full_roll_length_m", label: "Full roll (m)", type: "number" },
    { key: "low_stock_threshold_meters", label: "Low-stock (m)", type: "number" },
    { key: "low_stock_threshold_count", label: "Low-stock (units)", type: "number" },
    { key: "discontinued", label: "Discontinued", type: "switch" },
    { key: "discontinued_at", label: "Discontinued date", type: "date" },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  manufacturer: [
    { key: "name", label: "Name", type: "text" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "catalogue_url", label: "Catalogue URL", type: "text" },
  ],
  series: [
    { key: "name", label: "Name", type: "text" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "manufacturer_id", label: "Manufacturer", type: "manufacturer" },
  ],
  roll: [
    { key: "finish_reason", label: "Finish reason", type: "text" },
    { key: "reason", label: "Reason", type: "text" },
  ],
};

const ACTION_META = {
  create: { label: "Create", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Plus },
  update: { label: "Edit", cls: "bg-blue-50 text-blue-700 border-blue-200", Icon: PencilSimple },
  delete: { label: "Delete", cls: "bg-red-50 text-red-700 border-red-200", Icon: Trash },
  consume: { label: "Consume", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Prohibit },
};

const HIDE_FIELDS = new Set(["id", "created_at", "_id"]);
const FIELD_LABELS = {
  name: "Name", description: "Description", base_sku: "Base SKU", location: "Location",
  color_hex: "Colour", manufacturer_id: "Manufacturer", series_id: "Series",
  low_stock_threshold_meters: "Low-stock (m)", low_stock_threshold_count: "Low-stock (units)",
  current_count: "Stock (units)", inventory_type: "Type", notes: "Notes",
};

function Changes({ cr, maps }) {
  const payload = cr.payload || {};
  const snap = cr.snapshot || {};
  const display = (k, v) => {
    if (v == null || v === "") return "—";
    if (k === "manufacturer_id") return maps?.mfr?.[v] || String(v);
    if (k === "series_id") return maps?.series?.[v] || String(v);
    return String(v);
  };
  if (cr.action === "delete") {
    return <div className="text-sm text-slate-600">Requesting deletion of <b>{cr.label}</b> and all related data.</div>;
  }
  if (cr.action === "consume") {
    const parts = [];
    if (payload.batch_number) parts.push(`batch ${payload.batch_number}`);
    if (payload.location) parts.push(payload.location);
    return (
      <div className="text-sm text-slate-600" data-testid={`consume-detail-${cr.id}`}>
        Mark roll <b>{cr.label}</b> as <b className="text-amber-700">consumed</b> (depleted){parts.length ? ` — ${parts.join(" · ")}` : ""}.
        <div className="text-xs text-slate-500 mt-1">{payload.reason || "Not found during stock take"}. Approving will remove it from active inventory.</div>
      </div>
    );
  }
  const keys = Object.keys(payload).filter((k) => !HIDE_FIELDS.has(k));
  const rows = cr.action === "update"
    ? keys.filter((k) => JSON.stringify(payload[k]) !== JSON.stringify(snap[k]))
    : keys.filter((k) => payload[k] !== null && payload[k] !== "" && payload[k] !== undefined);
  if (rows.length === 0) return <div className="text-sm text-slate-500">No field changes.</div>;
  return (
    <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
      {rows.map((k) => (
        <div key={k} className="flex items-start gap-3 px-3 py-2 text-sm">
          <div className="w-32 shrink-0 text-[11px] font-mono uppercase tracking-wide text-slate-500">{FIELD_LABELS[k] || k}</div>
          {cr.action === "update" ? (
            <div className="flex-1 flex items-center gap-2 flex-wrap">
              <span className="line-through text-slate-400">{display(k, snap[k])}</span>
              <span className="text-slate-400">→</span>
              <span className="font-medium text-slate-800">{display(k, payload[k])}</span>
            </div>
          ) : (
            <div className="flex-1 font-medium text-slate-800">{display(k, payload[k])}</div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function UpdateRequests() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [maps, setMaps] = useState({ mfr: {}, series: {} });
  const [mfrList, setMfrList] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editPayload, setEditPayload] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/manufacturers"), api.get("/series")])
      .then(([m, s]) => {
        setMfrList(m.data || []);
        setSeriesList(s.data || []);
        setMaps({
          mfr: Object.fromEntries((m.data || []).map((x) => [x.id, x.name])),
          series: Object.fromEntries((s.data || []).map((x) => [x.id, x.name])),
        });
      })
      .catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = tab === "pending" ? { status: "pending" } : {};
      const { data } = await api.get("/change-requests", { params });
      setItems(tab === "pending" ? data : data.filter((c) => c.status !== "pending"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]);

  const approve = async (cr) => {
    setBusy(true);
    try {
      await api.post(`/change-requests/${cr.id}/approve`);
      toast.success("Change approved & applied");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const doReject = async () => {
    setBusy(true);
    try {
      await api.post(`/change-requests/${rejecting.id}/reject`, { reason });
      toast.success("Change rejected");
      setRejecting(null); setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const editableDefs = (cr) => {
    const defs = FIELD_DEFS[cr.entity_type] || [];
    return cr.action === "create" ? defs : defs.filter((d) => d.key in (cr.payload || {}));
  };

  const openEdit = (cr) => { setEditing(cr); setEditPayload({ ...(cr.payload || {}) }); };

  const coerce = (type, val) => {
    switch (type) {
      case "number": return val === "" || val == null ? null : Number(val);
      case "switch": return !!val;
      case "hex": return val ? String(val) : null;
      case "date": return val ? String(val) : null;
      default: return val == null ? "" : String(val);
    }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const defs = editableDefs(editing);
      const updated = { ...(editing.payload || {}) };
      defs.forEach((d) => { updated[d.key] = coerce(d.type, editPayload[d.key]); });
      await api.put(`/change-requests/${editing.id}`, { payload: updated });
      toast.success("Request updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSavingEdit(false); }
  };

  return (
    <div className="p-8 max-w-[1100px]" data-testid="update-requests-page">
      <header className="mb-8">
        <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Workspace · Approvals</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <Bell size={32} /> Update Requests
        </h1>
        <p className="text-sm text-slate-600 mt-2">Staff changes awaiting your review — catalogue edits and stock-take roll consumptions.</p>
      </header>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("pending")} data-testid="tab-pending"
          className={`px-4 py-2 text-sm font-medium border rounded-md ${tab === "pending" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>Pending</button>
        <button onClick={() => setTab("history")} data-testid="tab-history"
          className={`px-4 py-2 text-sm font-medium border rounded-md ${tab === "history" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>History</button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-16 text-center" data-testid="no-requests">
          <Check size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500">{tab === "pending" ? "No pending requests. You're all caught up." : "No resolved requests yet."}</div>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((cr) => {
            const meta = ACTION_META[cr.action] || ACTION_META.update;
            const ActionIcon = meta.Icon;
            return (
              <div key={cr.id} className="border border-slate-200 bg-white rounded-md p-5" data-testid={`request-${cr.id}`}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border inline-flex items-center gap-1 ${meta.cls}`}>
                        <ActionIcon size={11} /> {meta.label}
                      </span>
                      <span className="text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border bg-slate-50 text-slate-600 border-slate-200">{cr.entity_type}</span>
                      {cr.status !== "pending" && (
                        <span className={`text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border ${cr.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>{cr.status}</span>
                      )}
                    </div>
                    <div className="font-heading text-lg font-semibold text-slate-900">{cr.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                      <Clock size={12} /> {cr.requested_by_name || cr.requested_by_email} · {new Date(cr.created_at).toLocaleString()}
                      {cr.edited_at && <span className="text-[10px] font-mono uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200 rounded-sm px-1.5 py-0.5 ml-1" data-testid={`edited-badge-${cr.id}`}>edited</span>}
                    </div>
                  </div>
                  {cr.status === "pending" && (
                    <div className="flex gap-2 shrink-0">
                      {editableDefs(cr).length > 0 && (
                        <Button onClick={() => openEdit(cr)} disabled={busy} variant="outline" className="gap-1.5 rounded-md" data-testid={`edit-${cr.id}`}>
                          <PencilSimple size={16} /> Edit
                        </Button>
                      )}
                      <Button onClick={() => approve(cr)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5 rounded-md" data-testid={`approve-${cr.id}`}>
                        <Check size={16} /> Approve
                      </Button>
                      <Button onClick={() => { setRejecting(cr); setReason(""); }} disabled={busy} variant="outline" className="gap-1.5 rounded-md text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-${cr.id}`}>
                        <X size={16} /> Reject
                      </Button>
                    </div>
                  )}
                </div>
                <Changes cr={cr} maps={maps} />
                {cr.status === "rejected" && cr.reject_reason && (
                  <div className="text-xs text-red-600 mt-2">Reason: {cr.reject_reason}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject request</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm text-slate-600">Rejecting <b>{rejecting?.label}</b>. Add an optional reason for the requester.</div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Reason (optional)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. wrong SKU format" data-testid="reject-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)} className="rounded-md">Cancel</Button>
            <Button onClick={doReject} disabled={busy} className="bg-red-600 hover:bg-red-700 rounded-md" data-testid="confirm-reject">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit request before approving</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div className="text-sm text-slate-600">
                Editing <b>{editing.label}</b>. Changes are saved to the request — approve it afterwards to apply them.
              </div>
              {editableDefs(editing).length === 0 ? (
                <div className="text-sm text-slate-500">Nothing to edit for this request.</div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {editableDefs(editing).map((d) => {
                    const val = editPayload[d.key];
                    const set = (v) => setEditPayload((p) => ({ ...p, [d.key]: v }));
                    return (
                      <div key={d.key} className={d.type === "switch" ? "flex items-center justify-between gap-3" : "space-y-1.5"}>
                        <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">{d.label}</Label>
                        {d.type === "textarea" ? (
                          <Textarea value={val || ""} onChange={(e) => set(e.target.value)} rows={3} data-testid={`edit-field-${d.key}`} />
                        ) : d.type === "switch" ? (
                          <Switch checked={!!val} onCheckedChange={set} data-testid={`edit-field-${d.key}`} />
                        ) : d.type === "hex" ? (
                          <div className="flex items-center gap-2">
                            <input type="color" value={val || "#000000"} onChange={(e) => set(e.target.value)} className="w-10 h-9 rounded border border-slate-200 bg-white p-0.5 shrink-0" data-testid={`edit-field-${d.key}-color`} />
                            <Input value={val || ""} onChange={(e) => set(e.target.value)} placeholder="#RRGGBB" className="font-mono" data-testid={`edit-field-${d.key}`} />
                          </div>
                        ) : d.type === "date" ? (
                          <Input type="date" value={val || ""} onChange={(e) => set(e.target.value)} className="font-mono" data-testid={`edit-field-${d.key}`} />
                        ) : d.type === "number" ? (
                          <Input type="number" step="0.01" value={val ?? ""} onChange={(e) => set(e.target.value)} className="font-mono" data-testid={`edit-field-${d.key}`} />
                        ) : d.type === "manufacturer" ? (
                          <Select value={val || ""} onValueChange={(v) => setEditPayload((p) => ({ ...p, manufacturer_id: v, ...(editing.entity_type === "product" ? { series_id: "" } : {}) }))}>
                            <SelectTrigger data-testid={`edit-field-${d.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                            <SelectContent>{mfrList.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : d.type === "series" ? (
                          <Select value={val || ""} onValueChange={set}>
                            <SelectTrigger data-testid={`edit-field-${d.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                            <SelectContent>
                              {seriesList.filter((s) => !editPayload.manufacturer_id || s.manufacturer_id === editPayload.manufacturer_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : d.type === "invtype" ? (
                          <Select value={val || "rolls"} onValueChange={set}>
                            <SelectTrigger data-testid={`edit-field-${d.key}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="rolls">Rolls (meters)</SelectItem>
                              <SelectItem value="count">Count (units)</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input value={val || ""} onChange={(e) => set(e.target.value)} data-testid={`edit-field-${d.key}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-md">Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit || !editing || editableDefs(editing).length === 0} className="bg-slate-900 hover:bg-slate-800 rounded-md" data-testid="save-edit-btn">{savingEdit ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
